import type { TUnderlyingDataExportDto } from '@homepal/api-contract'

import {
  getUnderlyingData,
  getUnderlyingDataExport,
  startUnderlyingDataExport,
  TUnderlyingDataOptions,
  TUnderlyingDataSource
} from 'redux/api/UnderlyingData'

import { TDataDialogView } from './types'
import { TGlobalFilterApiInput } from 'types/GlobalGlobalFilter'
import { TMetricQueryParams } from 'types/GlobalMetric'

/**
 * Single entry point for fetching underlying data, regardless of source.
 * Every view goes through the unified underlying-data endpoint; this is the
 * one place on the frontend that knows how a view maps to a source. Only
 * metric sources carry settings in the request — the others resolve them
 * server-side from the stored entity.
 */
export function fetchUnderlyingData(
  view: TDataDialogView,
  queryParams: TMetricQueryParams,
  globalFilter: TGlobalFilterApiInput,
  options: TUnderlyingDataOptions
) {
  return getUnderlyingData(toSource(view), queryParams, globalFilter, options)
}

// How often the dialog asks about a running export, and for how long: the
// export runs for at most 15 minutes on the server.
const EXPORT_POLL_MS = 2000
const EXPORT_TIMEOUT_MS = 16 * 60 * 1000

function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

async function waitForExport(
  id: string,
  deadline: number
): Promise<TUnderlyingDataExportDto> {
  await wait(EXPORT_POLL_MS)

  const current = (await getUnderlyingDataExport(id)).data.data

  if (current.status !== 'running') {
    return current
  }

  if (Date.now() > deadline) {
    throw new Error('The export did not finish in time')
  }

  return waitForExport(id, deadline)
}

/**
 * Exports every row of a view to an Excel file on the server, and resolves
 * with its download link once the file is ready.
 */
export async function exportUnderlyingData(
  view: TDataDialogView,
  queryParams: TMetricQueryParams,
  globalFilter: TGlobalFilterApiInput,
  options: TUnderlyingDataOptions,
  fileName: string
): Promise<TUnderlyingDataExportDto> {
  const started = (
    await startUnderlyingDataExport(
      toSource(view),
      queryParams,
      globalFilter,
      options,
      fileName
    )
  ).data.data

  const finished = await waitForExport(
    started.id,
    Date.now() + EXPORT_TIMEOUT_MS
  )

  if (finished.status !== 'done' || !finished.url) {
    throw new Error('The export failed')
  }

  return finished
}

function toSource(view: TDataDialogView): TUnderlyingDataSource {
  switch (view.sourceType) {
  case 'metric':
    return {
      type: 'metric',
      id: view.id,
      settings: view.metric.settings
    }
  case 'key_metric':
    return { type: 'key_metric', id: view.id }
  case 'kpi_option':
    return { type: 'kpi_option', id: view.id }
  }
}
